import { checkoutShippingOptionSchema } from "@redotech/redo-conversion-checkout-shipping-common/checkout-shipping-option";
import { SesEventSchema } from "@redotech/redo-customer-event-common/events/ses-events";
import {
  LengthUnitSchema,
  WeightUnitSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { CustomParcelTypeSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { ChannelSchema } from "@redotech/redo-marketplace-common/channels";
import { Provider } from "@redotech/redo-order-common/provider";
import { YofiScoreSchema } from "@redotech/redo-order-common/yofi-score";
import {
  TrackableBillingStatus,
  TrackableEventType,
} from "@redotech/redo-order-tracking-trackable-schema-common/trackable-schema";
import { TrackerSchema } from "@redotech/redo-order-tracking-tracker-common/tracker";
import { PaymentModel } from "@redotech/redo-return-coverage-common/coverage";
import { ShopifyOrderSchema } from "@redotech/redo-shopify-order-common/shopify-order";
import { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { OrderRefundEventType } from "./order";
export const PermissionActionSchema = createEnum(["allow", "deny"], {
  enumName: "PermissionAction",
});

const PermissionEntrySchema_ = z.object({
  name: z.string(),
  action: PermissionActionSchema,
});
export const PermissionEntrySchema: z.ZodType<PermissionEntry> =
  PermissionEntrySchema_;
export interface PermissionEntry extends z.infer<
  typeof PermissionEntrySchema_
> {}

const NotificationChannelSettingsSchema_ = z.object({
  desktop: z.boolean(),
  email: z.boolean(),
  push: z.boolean(),
});
export const NotificationChannelSettingsSchema: z.ZodType<NotificationChannelSettings> =
  NotificationChannelSettingsSchema_;
export interface NotificationChannelSettings extends z.infer<
  typeof NotificationChannelSettingsSchema_
> {}

const UserNotificationsSchema_ = z.object({
  playSoundOnDesktopNotification: z.boolean(),
  mentionedInConversation: NotificationChannelSettingsSchema,
  assignedToConversation: NotificationChannelSettingsSchema,
  newMessageInAssignedConversation: NotificationChannelSettingsSchema,
  newMessageInUnassignedConversation: NotificationChannelSettingsSchema,
});
export const UserNotificationsSchema: z.ZodType<UserNotifications> =
  UserNotificationsSchema_;
export interface UserNotifications extends z.infer<
  typeof UserNotificationsSchema_
> {}

const CustomViewOrderingSchema_ = z.object({
  viewName: z.string().nullish(),
  view: z.string().nullish(),
});
export const CustomViewOrderingSchema: z.ZodType<CustomViewOrdering> =
  CustomViewOrderingSchema_;
export interface CustomViewOrdering extends z.infer<
  typeof CustomViewOrderingSchema_
> {}

const GetUserSchema_ = z.object({
  roles: z.array(z.string()),
  _id: z.record(z.string(), z.any()),
  email: z.string(),
  createdAt: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  name: z.string(),
  updatedAt: z.string(),
  image: z.array(z.any()),
  team: zExt.objectId(),
  permissions: z.array(PermissionEntrySchema),
  notifications: UserNotificationsSchema,
  customViewOrdering: z.array(CustomViewOrderingSchema).nullish(),
  supportV2ViewOrdering: z
    .array(z.object({ id: z.string(), type: z.enum(["view", "folder"]) }))
    .optional(),
});
export const GetUserSchema: z.ZodType<GetUser> = GetUserSchema_;
export interface GetUser extends z.infer<typeof GetUserSchema_> {}

// Update the TimelineEventSchema to use the newly created GetUserSchema
const TimelineEventSchema_ = z.object({
  customer: z
    .object({
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      email: z.string().nullish(),
      gravatarUrl: z.string().nullish(),
    })
    .nullish(),
  user: z.union([GetUserSchema, z.string(), zExt.objectId()]).nullish(),
  image: z.string().nullish(),
  isShipment: z.boolean(),
  _id: zExt.objectId().nullish(),
  message: z.string().nullish().nullish(),
  link: z
    .object({
      message: z.string().nullish(),
      orderNumber: z.string().nullish(),
      url: z.string().nullish(),
    })
    .nullish(),
  subMessage: z.string().nullish(),
  isSystem: z.boolean(),
  updatedAt: z.date().nullish(),
  createdAt: z.date().nullish(),
});
export const TimelineEventSchema: z.ZodType<TimelineEvent> =
  TimelineEventSchema_;
export interface TimelineEvent extends z.infer<typeof TimelineEventSchema_> {}

const OrderRefundEventSchema_ = z.object({
  createdAt: z.date(),
  type: createEnum(OrderRefundEventType, { enumName: "OrderRefundEventType" }),
});
export const OrderRefundEventSchema: z.ZodType<OrderRefundEvent> =
  OrderRefundEventSchema_;
export interface OrderRefundEvent extends z.infer<
  typeof OrderRefundEventSchema_
> {}

const OutboundLabelShippingAddressSchema_ = z.object({
  name: z.string().nullish(),
  address1: z.string(),
  address2: z.string().nullish(),
  city: z.string(),
  country: z.string().nullish(),
  country_code: z.string().nullish(),
  province: z.string().nullish(),
  province_code: z.string().nullish(),
  zip: z.string().nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  company: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string(),
});
export const OutboundLabelShippingAddressSchema: z.ZodType<OutboundLabelShippingAddress> =
  OutboundLabelShippingAddressSchema_;
export interface OutboundLabelShippingAddress extends z.infer<
  typeof OutboundLabelShippingAddressSchema_
> {}

const ShipmentParcelSchema_ = z.object({
  length: z.number().nullish().nullish(),
  width: z.number().nullish().nullish(),
  height: z.number().nullish().nullish(),
  weight: z.number(),
});
export const ShipmentParcelSchema: z.ZodType<ShipmentParcel> =
  ShipmentParcelSchema_;
export interface ShipmentParcel extends z.infer<typeof ShipmentParcelSchema_> {}

const AddressSchema_ = z.object({
  company: z.string().nullish().nullish(),
  name: z.string().nullish().nullish(),
  phone: z.string().nullish().nullish(),
  email: z.string().nullish().nullish(),
  street1: z.string().nullish().nullish(),
  street2: z.string().nullish().nullish(),
  city: z.string().nullish().nullish(),
  state: z.string().nullish().nullish(),
  zip: z.string(),
  country: z.string(),
  residential: z.boolean().optional(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

const RateSchema_ = z.object({
  id: z.string(),
  shipment_id: z.string(),
  rate: z.string(),
  currency: z.string(),
  carrier: z.string(),
  service: z.string(),
  delivery_date: z.string().nullish(),
  delivery_days: z.number().nullish(),
  delivery_date_guaranteed: z.boolean(),
  upcharge: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const RateSchema: z.ZodType<Rate> = RateSchema_;
export interface Rate extends z.infer<typeof RateSchema_> {}

const LabelSchema_ = z.object({ id: z.string(), label_url: z.string() });
export const LabelSchema: z.ZodType<Label> = LabelSchema_;
export interface Label extends z.infer<typeof LabelSchema_> {}

const ShipmentRatesSchema_ = z.object({
  shipmentId: z.string().nullish(),
  toAddress: AddressSchema,
  fromAddress: AddressSchema,
  parcel: ShipmentParcelSchema,
  mode: z.string(),
  rates: z.array(RateSchema),
  totalWeight: z.number().nullish(),
  weightUnit: z
    .union([z.literal("oz"), z.literal("lb"), z.literal("g"), z.literal("kg")])
    .nullish(),
});
export const ShipmentRatesSchema: z.ZodType<ShipmentRates> =
  ShipmentRatesSchema_;
export interface ShipmentRates extends z.infer<typeof ShipmentRatesSchema_> {}

const ServiceLevelSchema_ = z.object({
  carrier: z.string(),
  service: z.string(),
  upcharge: z
    .object({
      type: z.union([z.literal("amount"), z.literal("percentage")]),
      amount: z.number().nullish(),
      percentage: z.number().nullish(),
    })
    .nullish(),
});
export const ServiceLevelSchema: z.ZodType<ServiceLevel> = ServiceLevelSchema_;
export interface ServiceLevel extends z.infer<typeof ServiceLevelSchema_> {}

const BaseParcelSchema_ = z.object({ name: z.string() });
export const BaseParcelSchema: z.ZodType<BaseParcel> = BaseParcelSchema_;
export interface BaseParcel extends z.infer<typeof BaseParcelSchema_> {}

const CustomParcelSchema_ = BaseParcelSchema_.extend({
  type: CustomParcelTypeSchema,
  length: z.number(),
  width: z.number(),
  height: z.number(),
  lengthUnit: LengthUnitSchema,
  weight: z.number(),
  weightUnit: WeightUnitSchema,
});
export const CustomParcelSchema: z.ZodType<CustomParcel> = CustomParcelSchema_;
export interface CustomParcel extends z.infer<typeof CustomParcelSchema_> {}

const CarrierParcelSchema_ = BaseParcelSchema_.extend({
  carrier: z.string(),
  description: z.string(),
  dimensions: z.array(z.string()),
  human_readable: z.string(),
  max_weight: z.number().nullish(),
});
export const CarrierParcelSchema: z.ZodType<CarrierParcel> =
  CarrierParcelSchema_;
export interface CarrierParcel extends z.infer<typeof CarrierParcelSchema_> {}

export const ParcelSchema = z.union([CustomParcelSchema, CarrierParcelSchema]);

export const PaymentModelSchema = createEnum(PaymentModel, {
  enumName: "PaymentModel",
});

const CoverageSchema_ = z.object({
  exchange: z.boolean(),
  storeCredit: z.boolean(),
  refund: z.boolean(),
});
export const CoverageSchema: z.ZodType<Coverage> = CoverageSchema_;
export interface Coverage extends z.infer<typeof CoverageSchema_> {}

export const AttachmentStrategySchema = createEnum(AttachmentStrategy, {
  enumName: "AttachmentStrategy",
});

const OriginItemSchema_ = z.object({
  unitPrice: z.string(),
  preDiscountPrice: z.string().nullish(),
  currency: z.string().nullish(),
  tax: z.string(),
  originVariantId: z.number(),
  newVariantId: z.number(),
  applicableShopifyOrder: z.number().nullish(),
});
export const OriginItemSchema: z.ZodType<OriginItem> = OriginItemSchema_;
export interface OriginItem extends z.infer<typeof OriginItemSchema_> {}

const LineItemFulfillmentSchema_ = z.object({
  shopify_line_item_id: z.string().nullish(),
  fulfilled_at: z.date().nullish(),
  fulfillment_status: z.string().nullish(),
  shipment_status: z.string().nullish(),
  delivery_date: z.date().nullish(),
  delivered_at: z.date().nullish(),
});
export const LineItemFulfillmentSchema: z.ZodType<LineItemFulfillment> =
  LineItemFulfillmentSchema_;
export interface LineItemFulfillment extends z.infer<
  typeof LineItemFulfillmentSchema_
> {}

export const providerPrefixedOrderIdSchema_ = z.string();
export const providerPrefixedOrderIdSchema: z.ZodType<ProviderPrefixedOrderId> =
  providerPrefixedOrderIdSchema_;

/** Elevated order IDs by prefixing them with "bc:", "cs:", etc... Provider prefixed order IDs are unique on a given team. */
export type ProviderPrefixedOrderId = z.infer<
  typeof providerPrefixedOrderIdSchema_
>;

export const SourceSchema = ChannelSchema; // Can be extended to sources outside of redo marketplace channels

export const OrderLineItemSchema_ = z.object({
  id: z.string(),
  productId: zExt.objectId().nullish(),
  productName: z.string().nullish(),
  productDescription: z.string().nullish(),
  unitPrice: z.number().int().nonnegative().nullish(),
  quantity: z.number().int().positive().nullish(),
  totalPrice: z.number().int().nonnegative().nullish(),
  sku: z.string().nullish(),
  productVariantId: zExt.objectId().nullish(),
  variantTitle: z.string().nullish(),
  source: SourceSchema.nullish(),
  imageUrl: z.string().nullish(),
  // Mirrors HsaEligibility in @redotech/redo-conversion-hsa-common — that
  // package depends on redo/model, so importing it here would be a cycle.
  hsaEligibility: z
    .enum(["not_eligible", "eligible", "eligible_with_lmn"])
    .nullish(),
});
export const OrderLineItemSchema: z.ZodType<OrderLineItem> =
  OrderLineItemSchema_;
export interface OrderLineItem extends z.infer<typeof OrderLineItemSchema_> {}

export const OrderSchema_ = z.object({
  coverage: CoverageSchema.nullish(),
  protected: z.boolean(),
  packageProtected: z.boolean().nullish(),
  credits: z.number(),
  isExchangeOrder: z.boolean(),
  coverageEnabled: z.boolean().nullish(),
  _id: zExt.objectId(),
  shopify_id: providerPrefixedOrderIdSchema,
  team: zExt.objectId(),
  createdAt: z.date(),
  customer_name: z.string().nullish(),
  lineItemsFulfillment: z.array(LineItemFulfillmentSchema).nullish(),
  stalledInFulfillment: z.boolean().nullish(),
  forceProtected: z.boolean().nullish(),
  lineItems: z.array(OrderLineItemSchema).nullish(),
  discount: z
    .object({
      id: z.string().nullish(),
      description: z.string().nullish(),
      code: z.string().nullish(),
      expirationDateTime: z.string().nullish(),
    })
    .nullish(),
  shopify: ShopifyOrderSchema,
  shopifyCreatedAt: z.date(),
  shopifyUpdatedAt: z.date().nullish(),
  timeline: z.array(TimelineEventSchema),
  refundEvents: z.array(OrderRefundEventSchema).nullish(),
  updatedAt: z.date(),
  __v: z.number(),
  normalized_postal: z.array(z.string()).nullish(),
  redoEligible: z.boolean().nullish(),
  redoPackageProtectionEligible: z.boolean().nullish(),
  redoFinalSaleReturnsEligible: z.boolean().nullish(),
  finalSaleReturnsProtected: z.boolean().nullish(),
  originItems: z.array(OriginItemSchema).nullish(),
  currentEmailFlows: z
    .array(
      z.object({
        emailFlowId: z.string(),
        currentStep: z.number(),
        continueDate: z.string(),
      }),
    )
    .nullish(),
  trackers: z
    .array(
      z.object({
        _tracker: TrackerSchema,
        fulfillmentID: z.string().nullish(),
      }),
    )
    .nullish(),
  trackingAnalytics: z
    .object({
      email: z.array(SesEventSchema),
      page: z.array(
        z.object({
          url: z.string(),
          eventType: createEnum(TrackableEventType, {
            enumName: "TrackableEventType",
          }),
          image: z.string().nullish(),
          createdAt: z.date(),
        }),
      ),
      adViews: z.array(z.object({})).nullish(),
      adClicks: z.number().nullish(),
      pageViews: z.number().nullish(),
      upsell: z.array(z.object({})).nullish(),
    })
    .nullish(),
  preProtected: z.boolean().nullish(),
  prePackageProtected: z.boolean().nullish(),
  trackingTextsSent: z
    .array(z.object({ sid: z.string(), mms: z.boolean(), sentAt: z.date() }))
    .nullish(),
  trackingEmailsSent: z
    .array(
      z.object({
        emailId: z.string(),
        status: z.string().optional(),
        sentAt: z.date().optional(),
        s3URL: z.string().nullish(),
        trigger: z.string().optional(),
        trackingCode: z.string().optional(),
      }),
    )
    .nullish(),
  trackingWebhooksSent: z
    .array(
      z.object({
        destinationUrl: z.string(),
        sentAt: z.date(),
        automationStepId: z.string(),
        statusCode: z.number().optional(),
        success: z.boolean(),
        trigger: z.string(),
      }),
    )
    .nullish(),
  trackingBillingStatus: createEnum(TrackableBillingStatus, {
    enumName: "TrackingBillingStatus",
  }).nullish(),
  yofiScores: z.record(z.string(), YofiScoreSchema).nullish(),
  packages: z.array(z.any()).nullish(),
  trackingTimeline: z
    .array(
      z.object({
        status: z.string(),
        statusDetail: z.string(),
        trackerId: z.string(),
        estDeliveryDate: z.string().nullish(),
        createdAt: z.date().nullish(),
        updatedAt: z.date().nullish(),
        carrier: z.string().nullish(),
        fulfillmentId: z.string().nullish(),
      }),
    )
    .nullish(),
  forceReplicationAt: z.date().nullish(),
  shipmentRates: ShipmentRatesSchema.nullish(),
  parcels: z.array(ParcelSchema).nullish(),
  labels: z
    .array(
      z.object({
        label: LabelSchema,
        rate: RateSchema,
        printed: z.boolean().nullish(),
      }),
    )
    .nullish(),
  provider: createEnum(Provider, { enumName: "Provider" }).default(
    Provider.SHOPIFY,
  ),
  // `.catch` keeps a legacy/malformed option from failing the whole order parse
  // — duties are a display enhancement and must never break loading the order.
  selectedRedoCheckoutShippingOptions: z
    .array(checkoutShippingOptionSchema)
    .nullish()
    .catch(undefined),
  tnplContract: zExt.objectId().nullish(),
  parentOrderId: zExt.objectId().nullish(),
  providerIntegrationId: zExt.objectId().nullish(),
  buyerId: z.string().nullish(),
  locationId: z.string().nullish(),
  coverageAnalytics: z
    .object({ redoOptedInFromCart: z.boolean().nullish() })
    .nullish(),
});
export const OrderSchema: z.ZodType<Order> = OrderSchema_;
export interface Order extends z.infer<typeof OrderSchema_> {}
